import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { theme } from 'antd';
import { Button, Spinner } from '@shared/ui';
import { useWsState } from '@shared/ws';
import { useSessionStore } from '@entities/session';
import {
  useConversationQuery,
  useMarkReadMutation,
  useMessagesInfiniteQuery,
  useSendMessageMutation,
  pickCounterpart,
} from '@entities/chat';
import { Composer } from './composer';
import { MessageBubble } from './message-bubble';
import { useConversationStream } from '../model/use-conversation-stream';

interface ChatWindowProps {
  conversationId: string;
}

export function ChatWindow({ conversationId }: ChatWindowProps) {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const wsState = useWsState();
  const ownUserId = useSessionStore((s) => s.user?.id);
  const [focused, setFocused] = useState(true);

  const conversationQuery = useConversationQuery(conversationId);
  const messagesQuery = useMessagesInfiniteQuery(conversationId);
  const sendMutation = useSendMessageMutation(conversationId);
  const markRead = useMarkReadMutation();
  const { sendViaWs, markReadViaWs } = useConversationStream({
    conversationId,
    focused,
    ownUserId,
  });

  const allMessages = useMemo(() => {
    const items = messagesQuery.data?.pages.flatMap((p) => p.items) ?? [];
    return [...items].reverse();
  }, [messagesQuery.data]);

  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [allMessages.length]);

  useEffect(() => {
    const onFocus = () => setFocused(true);
    const onBlur = () => setFocused(false);
    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  useEffect(() => {
    if (focused && conversationId) {
      if (!markReadViaWs()) markRead.mutate(conversationId);
    }
  }, [conversationId, focused, markRead, markReadViaWs]);

  const onSend = async (body: string, attachments: string[]) => {
    // Prefer WS for low-latency delivery; fall back to REST when disconnected.
    if (sendViaWs(body, attachments)) return;
    await sendMutation.mutateAsync({ body, attachments });
  };

  const conversation = conversationQuery.data;
  const cp = conversation ? pickCounterpart(conversation, ownUserId) : null;

  return (
    <div
      className="flex h-full flex-col"
      style={{ background: token.colorBgContainer, minWidth: 0 }}
    >
      <header
        className="flex items-center justify-between gap-3 px-4 py-3"
        style={{ borderBottom: `1px solid ${token.colorSplit}` }}
      >
        <div className="flex flex-col">
          <span className="font-medium">{cp?.name || '—'}</span>
          {conversation?.counterpartOnline ? (
            <span className="text-xs" style={{ color: token.colorSuccess }}>{t('chats.online')}</span>
          ) : (
            <span className="text-xs" style={{ color: token.colorTextTertiary }}>{t('chats.offline')}</span>
          )}
        </div>
        <span className="text-xs capitalize" style={{ color: token.colorTextTertiary }}>{wsState}</span>
      </header>

      <div
        className="flex-1 overflow-y-auto px-4 py-3"
        style={{ background: token.colorFillQuaternary }}
      >
        {messagesQuery.isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <>
            {messagesQuery.hasNextPage ? (
              <div className="flex justify-center pb-3">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  loading={messagesQuery.isFetchingNextPage}
                  onClick={() => void messagesQuery.fetchNextPage()}
                >
                  {t('chats.loadOlder')}
                </Button>
              </div>
            ) : null}
            <div className="flex flex-col gap-2">
              {allMessages.map((m) => (
                <MessageBubble key={m.id} message={m} own={Boolean(ownUserId && m.senderId === ownUserId)} />
              ))}
            </div>
            <div ref={bottomRef} />
          </>
        )}
      </div>

      <Composer onSend={onSend} disabled={!conversationId} />
    </div>
  );
}
